import { setFormattingLanguage } from "./formattingLocale";
import { SourceCopyProvider, translateLoadedSource } from "./LocalizedCopy";
import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import api from "../api";
import { DEFAULT_LANG, LANGS, TRANSLATIONS } from "./translations";
import { LANGUAGE_STORAGE_KEY, initialLanguage, saveLanguage, supportedLanguage } from "./languageDetection";

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: (key, fallback) => fallback || key,
  langs: LANGS,
});

export function LanguageProvider({ children }) {
  const [selection, setSelection] = useState(() => { const initial = initialLanguage(); setFormattingLanguage(initial.lang); return initial; });
  const manualChoice = useRef(selection.manual);
  const { lang, manual } = selection;

  const setLang = useCallback((next) => {
    const safe = supportedLanguage(next);
    if (!safe) return;
    // Mark the choice synchronously, before any pending detection can finish.
    manualChoice.current = true;
    setFormattingLanguage(safe);
    setSelection({ lang: safe, manual: true });
    saveLanguage(safe);
  }, []);

  useEffect(() => {
    if (manual) return undefined;
    const controller = new AbortController();
    api.get("/api/locale/", { signal: controller.signal, timeout: 4000 })
      .then(({ data }) => {
        const detected = supportedLanguage(data?.language);
        if (detected && !controller.signal.aborted && !manualChoice.current) {
          setFormattingLanguage(detected);
          setSelection({ lang: detected, manual: false });
        }
      })
      .catch(() => {
        // Keep the French default when country detection is unavailable.
      });
    return () => controller.abort();
  }, [manual]);

  useEffect(() => {
    const syncChoice = (event) => {
      if (event.key !== LANGUAGE_STORAGE_KEY && event.key !== null) return;
      const next = initialLanguage();
      manualChoice.current = next.manual;
      setFormattingLanguage(next.lang);
      setSelection(next);
    };
    window.addEventListener("storage", syncChoice);
    return () => window.removeEventListener("storage", syncChoice);
  }, []);

  useLayoutEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback(
    (key, fallback) => {
      const k = String(key || "");
      const dict = TRANSLATIONS[lang] || TRANSLATIONS[DEFAULT_LANG] || {};
      const base = TRANSLATIONS[DEFAULT_LANG] || {};
      const text = dict[k] || base[k] || fallback || k;
      return lang === "en" ? translateLoadedSource(text, lang) : text;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t, langs: LANGS }), [lang, setLang, t]);
  return <LanguageContext.Provider value={value}><SourceCopyProvider lang={lang}>{children}</SourceCopyProvider></LanguageContext.Provider>;
}

export function useI18n() {
  return useContext(LanguageContext);
}
